import React from 'react';
import { TypeSectionBanner } from "../../@types/sectionTypes";
import { BANNER_TYPE } from "../../../globals/constants";
import SectionInnerPageBanner from "../../sections/common/banner";
import PolicyLayout from "../../components/PolicyLayout";

const TermsOfServicePage = () => {
    const bannerData: TypeSectionBanner = {
        title: "Terms of Service",
        crumb: "Terms of Service",
        image: "/assets/images/banner/1.jpg"
    };

    const sections = [
        { id: "introduction", title: "Introduction and Acceptance" },
        { id: "company-info", title: "Company Information" },
        { id: "services", title: "Services Provided" },
        { id: "user-accounts", title: "User Accounts and Registration" },
        { id: "acceptable-use", title: "Acceptable Use Policy" },
        { id: "intellectual-property", title: "Intellectual Property Rights" },
        { id: "payment-terms", title: "Payment Terms and Billing" },
        { id: "service-level", title: "Service Level Agreements" },
        { id: "data-protection", title: "Data Protection and Privacy" },
        { id: "limitation-liability", title: "Limitation of Liability" },
        { id: "indemnification", title: "Indemnification" },
        { id: "confidentiality", title: "Confidentiality" },
        { id: "termination", title: "Termination" },
        { id: "dispute-resolution", title: "Dispute Resolution" },
        { id: "general-provisions", title: "General Provisions" },
        { id: "contact", title: "Contact Information" }
    ];

    return (
        <div className="terms-of-service-page">
            <SectionInnerPageBanner _data={bannerData} _type={BANNER_TYPE.COMPACT} />
            
            <PolicyLayout title="Terms of Service" sections={sections}>
                <div className="text-center mb-5">
                    <p><strong>Last updated: {new Date().toLocaleDateString()}</strong></p>
                </div>

                <div id="introduction" className="section">
                    <h2>1. Introduction and Acceptance</h2>
                    <p>
                        Welcome to Anathoth Technologies Ltd ("Company," "we," "our," or "us"). These Terms of Service ("Terms") 
                        constitute a legally binding agreement between you ("User," "you," or "your") and Anathoth Technologies Ltd, 
                        a company incorporated in Uganda (Registration Number: 80034461905872).
                    </p>
                    <p>
                        By accessing or using our website, applications, or services, you agree to be bound by these Terms. 
                        If you do not agree to these Terms, please do not use our services.
                    </p>
                </div>

                <div id="company-info" className="section">
                    <h2>2. Company Information</h2>
                    <div className="company-info bg-light p-4 rounded mb-4">
                        <p><strong>Legal Name:</strong> Anathoth Technologies Ltd</p>
                        <p><strong>Trading Name:</strong> Anathoth</p>
                        <p><strong>Registration Number:</strong> 80034461905872</p>
                        <p><strong>Jurisdiction:</strong> Uganda</p>
                        <p><strong>Contact Email:</strong> info@theanathoth.com</p>
                        <p><strong>Phone:</strong> +256 744 666608</p>
                    </div>
                </div>

                <div id="services" className="section">
                    <h2>3. Services Provided</h2>
                    <p>Anathoth Technologies Ltd provides comprehensive technology solutions including:</p>
                    <ul>
                        <li><strong>Custom Software Development:</strong> Bespoke software solutions tailored to client requirements</li>
                        <li><strong>Web Application Development:</strong> Responsive web platforms and applications</li>
                        <li><strong>Mobile Application Development:</strong> Native and cross-platform mobile solutions</li>
                        <li><strong>System Integration:</strong> Connecting and optimizing existing systems</li>
                        <li><strong>Payment Gateway Solutions:</strong> Secure payment processing integrations</li>
                        <li><strong>IT Consulting Services:</strong> Strategic technology advisory and implementation</li>
                        <li><strong>Digital Transformation:</strong> Modernizing business processes through technology</li>
                        <li><strong>Cloud Solutions:</strong> Cloud migration, hosting, and management services</li>
                        <li><strong>Technical Support:</strong> Ongoing maintenance and support services</li>
                        <li><strong>Training Services:</strong> Professional development and technical training</li>
                    </ul>
                </div>

                <div id="user-accounts" className="section">
                    <h2>4. User Accounts and Registration</h2>
                    
                    <h3>Account Creation</h3>
                    <p>To access certain services, you may be required to create an account. You agree to provide accurate, current, and complete information during registration, maintain and promptly update your account information, maintain the security and confidentiality of your login credentials, accept responsibility for all activities under your account, and notify us immediately of any unauthorized use of your account.</p>
                    
                    <h3>Account Termination</h3>
                    <p>We reserve the right to suspend or terminate accounts that violate these Terms or engage in prohibited activities.</p>
                </div>

                <div id="acceptable-use" className="section">
                    <h2>5. Acceptable Use Policy</h2>
                    <p>You agree NOT to use our services for any unlawful purpose or in any way that could harm our systems or other users. Prohibited activities include:</p>
                    <ul>
                        <li>Violating any applicable laws or regulations</li>
                        <li>Infringing on intellectual property rights</li>
                        <li>Transmitting malicious code, viruses, or harmful software</li>
                        <li>Attempting to gain unauthorized access to our systems</li>
                        <li>Engaging in spamming, phishing, or other deceptive practices</li>
                        <li>Harassment, discrimination, or abusive behavior</li>
                        <li>Interfering with or disrupting our services</li>
                        <li>Reverse engineering or attempting to extract source code</li>
                        <li>Using our services to compete directly with us</li>
                    </ul>
                </div>

                <div id="intellectual-property" className="section">
                    <h2>6. Intellectual Property Rights</h2>
                    
                    <h3>Our Rights</h3>
                    <p>All intellectual property rights in our services, including software, designs, trademarks, and content, remain our property or that of our licensors. You may not copy, modify, or distribute our proprietary materials, use our trademarks without written permission, create derivative works based on our services, or remove or alter proprietary notices.</p>
                    
                    <h3>Client Work Product</h3>
                    <p>For custom development projects, you retain ownership of your proprietary business logic and data, while we retain rights to general methodologies, tools, and frameworks. Specific ownership terms will be detailed in project agreements, and we may retain rights to use general knowledge gained for future projects.</p>
                </div>

                <div id="payment-terms" className="section">
                    <h2>7. Payment Terms and Billing</h2>
                    
                    <h3>Fees and Payments</h3>
                    <p>All fees are quoted in USD unless otherwise specified. Payment terms are Net 30 days unless otherwise agreed. Late payments may incur interest charges at 1.5% per month, and all fees are non-refundable except as explicitly stated.</p>
                    
                    <h3>Subscription Services</h3>
                    <p>Subscription fees are billed in advance with automatic renewal unless cancelled with 30 days notice. Price changes require 60 days advance notice.</p>
                </div>

                <div id="service-level" className="section">
                    <h2>8. Service Level Agreements</h2>
                    
                    <h3>Availability</h3>
                    <p>We strive to maintain 99.5% uptime for hosted services, excluding scheduled maintenance.</p>
                    
                    <h3>Support</h3>
                    <p>Our business hours are Monday-Friday, 8 AM - 5 PM EAT. Critical issues receive response within 4 hours, while general inquiries are addressed within 24 hours. Emergency support is available for critical production issues.</p>usiness
                </div>

                <div id="data-protection" className="section">
                    <h2>9. Data Protection and Privacy</h2>
                    <p>
                        Your privacy is important to us. Our collection and use of personal information is governed by our 
                        <a href="/privacy-policy" className="text-primary">Privacy Policy</a>, which is incorporated into these Terms by reference.
                    </p>
                    <ul>
                        <li>We comply with applicable data protection laws</li>
                        <li>We implement appropriate security measures</li>
                        <li>We do not sell personal information to third parties</li>
                        <li>You retain ownership of your business data</li>
                    </ul>
                </div>

                <div id="limitation-liability" className="section">
                    <h2>10. Limitation of Liability</h2>
                    <p><strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong></p>
                    <ul>
                        <li>Our total liability for any claim shall not exceed the amount paid by you for the specific service in the 12 months preceding the claim</li>
                        <li>We are not liable for indirect, incidental, consequential, or punitive damages</li>
                        <li>We are not liable for lost profits, data loss, or business interruption</li>
                        <li>Force majeure events are excluded from liability</li>
                        <li>Third-party service failures are excluded from our liability</li>
                    </ul>
                </div>

                <div id="indemnification" className="section">
                    <h2>11. Indemnification</h2>
                    <p>You agree to indemnify and hold harmless Anathoth Technologies Ltd from any claims, damages, or expenses arising from:</p>
                    <ul>
                        <li>Your use of our services in violation of these Terms</li>
                        <li>Your content or data that infringes third-party rights</li>
                        <li>Your negligent or wrongful acts</li>
                        <li>Your violation of applicable laws</li>
                    </ul>
                </div>

                <div id="confidentiality" className="section">
                    <h2>12. Confidentiality</h2>
                    <p>Both parties agree to maintain the confidentiality of proprietary information shared during the business relationship:</p>
                    <ul>
                        <li>Confidential information includes technical data, business processes, and strategic information</li>
                        <li>Confidentiality obligations survive termination of services</li>
                        <li>Standard exceptions apply (publicly known information, independently developed, etc.)</li>
                    </ul>
                </div>

                <div id="termination" className="section">
                    <h2>13. Termination</h2>
                    
                    <h3>Termination by You</h3>
                    <p>You may terminate services with 30 days written notice. Early termination may result in termination fees.</p>
                    
                    <h3>Termination by Us</h3>
                    <p>We may terminate services immediately for material breach of these Terms, non-payment of fees, illegal or harmful activities, or insolvency or bankruptcy proceedings.</p>
                    
                    <h3>Effect of Termination</h3>
                    <p>Upon termination, you remain liable for all accrued fees, we will provide reasonable data export assistance, confidentiality obligations continue, and survival clauses remain in effect.</p>
                </div>

                <div id="dispute-resolution" className="section">
                    <h2>14. Dispute Resolution</h2>
                    
                    <h3>Governing Law</h3>
                    <p>These Terms are governed by the laws of Uganda.</p>
                    
                    <h3>Dispute Resolution Process</h3>
                    <p>Disputes will be resolved through good faith negotiations for 30 days. If negotiation fails, binding mediation in Kampala, Uganda will be pursued. Final disputes are resolved through arbitration under Uganda Arbitration Rules.</p>
                    
                    <h3>Jurisdiction</h3>
                    <p>Uganda courts have exclusive jurisdiction for any legal proceedings.</p>
                </div>

                <div id="general-provisions" className="section">
                    <h2>15. General Provisions</h2>
                    
                    <h3>Force Majeure</h3>
                    <p>Neither party is liable for delays or failures due to circumstances beyond reasonable control, including natural disasters, pandemics, or acts of God, government actions, war, or terrorism, internet or infrastructure failures, and labor strikes or disputes.</p>
                    
                    <h3>Modifications to Terms</h3>
                    <p>We reserve the right to modify these Terms at any time. Changes will be effective upon email notification to registered users, prominent website notice, or 30 days advance notice for material changes. Continued use of services after changes constitutes acceptance of modified Terms.</p>
                    
                    <h3>Severability</h3>
                    <p>If any provision of these Terms is found invalid or unenforceable, the remaining provisions remain in full force and effect.</p>
                    
                    <h3>Entire Agreement</h3>
                    <p>These Terms, together with our Privacy Policy and any signed service agreements, constitute the entire agreement between the parties.</p>
                </div>

                <div id="contact" className="section">
                    <h2>16. Contact Information</h2>
                    <p>For questions about these Terms of Service, please contact us or drop us an email.</p>
                    <p><strong>Contact Information:</strong></p>
                    <ul>
                        <li><strong>Email:</strong> info@theanathoth.com</li>
                        <li><strong>Phone:</strong> +256 744 666608</li>
                        <li><strong>Address:</strong> 1 Kololo Hill Drive, Kampala, Uganda</li>
                    </ul>
                    <p><em>Last updated on 01 January, 2025</em></p>
                </div>
            </PolicyLayout>
        </div>
    );
};

export default TermsOfServicePage; 